<template>
  <b-sidebar
    id="user-detail"
    :visible="Boolean(user)"
    right
    shadow
    backdrop
    width="440px"
    header-class="user-detail__header"
    body-class="user-detail__body"
    @hidden="$emit('close')"
  >
    <template #title>
      <div v-if="user">
        <div class="user-detail__name">
          <template v-if="user.persona">
            {{ user.persona.nombre }} {{ user.persona.primer_apellido }}
            {{ user.persona.segundo_apellido }}
          </template>
        </div>
        <div class="user-detail__email">{{ user.email }}</div>
      </div>
    </template>

    <div v-if="user" class="px-3 pb-3">
      <!-- Estado -->
      <div class="mb-3">
        <b-badge :variant="user.is_disabled ? 'danger' : 'success'" class="mr-1">
          {{ user.is_disabled ? 'Deshabilitado' : 'Habilitado' }}
        </b-badge>
        <b-badge v-if="!loading" variant="light" class="mr-1">{{ lastAccessText }}</b-badge>
      </div>

      <!-- Datos -->
      <h6 class="user-detail__section">Datos</h6>
      <dl class="user-detail__data">
        <dt>Profesión</dt>
        <dd>{{ empleado.profesion || '—' }}</dd>

        <dt>Fecha de ingreso</dt>
        <dd>{{ empleado.fecha_ingreso || '—' }}</dd>

        <template v-if="!loading">
          <dt>Unidad administrativa</dt>
          <dd>
            <span v-for="u in unidades" :key="u.id" class="d-block">
              {{ u.nombre }}
            </span>
            <span v-if="!unidades.length">—</span>
          </dd>

          <dt>Capítulos</dt>
          <dd>
            <b-badge v-for="c in capitulos" :key="c.id" variant="light" class="mr-1">
              {{ c.nombre }}
            </b-badge>
            <span v-if="!capitulos.length">—</span>
          </dd>

          <dt>Creación</dt>
          <dd>{{ $formatDate(user.fecha_creacion) }}</dd>

          <dt>Última modificación</dt>
          <dd>{{ $formatDate(user.ultima_modificacion) }}</dd>
        </template>
      </dl>

      <!-- Roles -->
      <h6 class="user-detail__section">Roles</h6>
      <div class="mb-3">
        <b-badge
          v-for="role in user.roles_checklist"
          :key="role.name"
          :variant="role.name === 'admin' ? 'success' : 'primary'"
          class="mr-1 mb-1"
        >
          {{ role.name }}
        </b-badge>
        <span v-if="!user.roles_checklist.length" class="text-danger small">Sin roles asignados</span>
      </div>

      <!-- Permisos agrupados -->
      <div v-if="loading" class="text-center text-muted small py-4">
        <b-spinner small class="mr-1"></b-spinner> Cargando permisos y adscripción…
      </div>

      <template v-else>
        <h6 class="user-detail__section">
          Permisos
          <small class="text-muted">({{ permissionNames.length }})</small>
        </h6>
        <p v-if="!permissionNames.length" class="text-danger small">
          Sin permisos: puede entrar, pero no verá ninguna sección.
        </p>
        <div v-for="group in permissionGroups" :key="group.name" class="user-detail__group">
          <button
            type="button"
            class="user-detail__group-toggle"
            @click="toggle(group.name)"
          >
            <span>{{ group.name }}</span>
            <b-badge variant="light" pill>{{ group.items.length }}</b-badge>
          </button>
          <div v-if="open[group.name]" class="user-detail__chips">
            <span v-for="p in group.items" :key="p" class="user-detail__chip">
              {{ p }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </b-sidebar>
</template>

<script>
// Respaldo para agrupar permisos por su nombre cuando el API no manda el módulo.
const GROUPS = [
  { name: 'Usuarios, roles y permisos', match: /(usuarios|empleados|roles|permisos)$/ },
  { name: 'Bitácora', match: /bitacoras/ },
  { name: 'Pólizas', match: /polizas?\b/ },
  { name: 'Reportes', match: /reporte/ },
  { name: 'Saldos', match: /saldos/ },
  { name: 'Planeación y proyección', match: /(proyeccion|salarios|jubilaciones|matriz|presupuestos?$|analisis presupuesto)/ },
  { name: 'Nómina', match: /nomina/ },
  { name: 'Recurso federal y ampliaciones', match: /(federal|ampliacion|estatal)/ },
  { name: 'Traspasos y suficiencia', match: /(traspasos?|suficiencia|disponibilidad)/ },
  { name: 'Momentos presupuestales', match: /presupuesto (aprobado|modificado|comprometido|devengado|ejercido|pagado|disponible)/ },
  { name: 'Catálogos', match: /(unidades administrativas|fuentes financiamiento|proyectos|partidas|prestaciones|periodos fiscales|techos financieros|claves presupuestales|tipos? de gasto|analitico)/ },
  { name: 'Otros', match: /.*/ }
]

export default {
  name: 'UserDetail',

  props: {
    user: {
      type: Object,
      default: null
    },

    // Mientras llega el usuario completo solo se muestra lo que trae la fila de la tabla.
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      open: {}
    }
  },

  computed: {
    empleado () {
      return (this.user && this.user.empleado) || {}
    },

    unidades () {
      return this.empleado.unidades_administrativas_nombres || []
    },

    capitulos () {
      return this.empleado.capitulos_nombres || []
    },

    lastAccessText () {
      if (!this.user || this.user.ultimo_acceso === 'Sin registro de ingreso') return 'Sin registro de ingreso'
      return `Último acceso: ${this.$formatDate(this.user.ultimo_acceso)}`
    },

    permissionNames () {
      if (!this.user || !this.user.permissions) return []
      return this.user.permissions.map(p => p.name).sort()
    },

    /**
     * Agrupa por el módulo que manda el servidor; si no viene (versión vieja
     * del API) lo deduce del nombre.
     */
    permissionGroups () {
      if (!this.user || !this.user.permissions) return []

      const groups = {}
      this.user.permissions.forEach(p => {
        const name = p.modulo || GROUPS.find(g => g.match.test(p.name)).name
        if (!groups[name]) groups[name] = { name, items: [] }
        groups[name].items.push(p.name)
      })

      return Object.values(groups).map(g => ({ ...g, items: g.items.sort() }))
    }
  },

  watch: {
    'user.id' () {
      this.open = {}
    }
  },

  methods: {
    toggle (name) {
      this.$set(this.open, name, !this.open[name])
    }
  }
}
</script>

<style lang="scss" scoped>
// Encima de la barra superior (z-index 1039) y debajo de los modales (1050).
.b-sidebar-outer {
  z-index: 1045;
}

::v-deep .user-detail__header {
  border-bottom: 1px solid #e3e6ec;
  padding: 1rem;
  align-items: flex-start;

  .b-sidebar-header,
  strong {
    flex-grow: 1;
    text-align: left;
  }
}

.user-detail__name {
  font-size: 1rem;
  font-weight: 600;
  color: #212832;
}

.user-detail__email {
  font-size: 0.8rem;
  font-weight: 400;
  color: #69707a;
}

.user-detail__section {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #69707a;
  margin: 1rem 0 0.5rem;
}

.user-detail__data {
  display: grid;
  grid-template-columns: 9.5rem 1fr;
  gap: 0.25rem 0.75rem;
  font-size: 0.8rem;
  margin-bottom: 0;

  dt {
    font-weight: 500;
    color: #69707a;
  }

  dd {
    margin: 0;
    color: #212832;
  }
}

.user-detail__group {
  border: 1px solid #e3e6ec;
  border-radius: 0.35rem;
  margin-bottom: 0.4rem;
}

.user-detail__group-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.6rem;
  background: none;
  border: 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #212832;
  text-align: left;

  &:focus {
    outline: none;
  }
}

.user-detail__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0 0.6rem 0.6rem;
}

.user-detail__chip {
  font-size: 0.7rem;
  line-height: 1.4;
  padding: 0.1rem 0.45rem;
  border-radius: 1rem;
  background: #eef2ff;
  color: #3a4bb3;
}
</style>
